import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "The 10 most common beginner investing mistakes are: trying to time the market, chasing hot stocks, panic selling in downturns, ignoring fees, over-trading, lacking diversification, using leverage, ignoring tax-advantaged accounts, taking tips from social media without research, and not having a plan. Avoiding these alone puts you ahead of most retail investors.";

const FAQS = [
  { q: 'What is the single biggest mistake?', a: 'Selling during downturns. The S&P 500 has had 15+ corrections of 10% or more since 1980, and every single one was followed by a recovery. Selling at the bottom locks in losses and misses the rebound.' },
  { q: 'Are fees really that important?', a: 'Yes. A 1% extra annual fee on a $100k portfolio over 30 years costs roughly $200,000 in lost growth. ETF expense ratios under 0.1% are achievable today; pay attention.' },
  { q: 'How much diversification is enough?', a: 'A single broad-market ETF (VTI, VT) is enough for most beginners. As your portfolio grows, you can layer in international and bond exposure. Owning 50 individual stocks isn\'t required; one good index fund can be.' },
  { q: 'Is it really wrong to follow stock tips from social media?', a: 'Not always wrong, but always risky. Most viral tips are either pumped intentionally, late, or based on incomplete information. Use them as starting points for research, not buy signals.' },
  { q: 'What\'s the simplest plan I can have?', a: 'Auto-transfer a fixed amount into a broad index ETF every month. Don\'t check the balance more than monthly. That\'s it. The complexity is what trips most beginners up.' },
];

export default function InvestingMistakesBeginnersMakePage() {
  return (
    <BlogArticleLayout
      slug="investing-mistakes-beginners-make"
      title="10 Investing Mistakes Beginners Make (And How to Avoid Them)"
      description="The 10 mistakes that quietly drain returns for first-time investors, plus a one-line fix for each one."
      keywords="investing mistakes, beginner investing mistakes, common investing mistakes, mistakes new investors make, investing for beginners, what not to do investing"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['how-to-start-investing-with-100-dollars', 'paper-trading', 'index-funds-vs-stocks']}
    >
      <section>
        <h2>What are the most common beginner mistakes?</h2>
        <p>
          Beginners don't fail because they pick bad stocks. They fail because they make the same handful of behavioural and structural mistakes over and over.
          Here are the ten that show up most often, and the one-line fix for each.
        </p>
      </section>

      <section>
        <h2>Mistakes 1–5: behavioural traps</h2>
        <ul>
          <li><strong>1. Trying to time the market.</strong> Even professionals usually fail. <em>Fix:</em> dollar-cost average — buy the same amount on the same day each month.</li>
          <li><strong>2. Chasing hot stocks.</strong> By the time you hear about it, the move is mostly over. <em>Fix:</em> if you can't explain why you're buying, don't.</li>
          <li><strong>3. Panic selling in downturns.</strong> The 2020 COVID crash erased 34% in a month, then recovered in five months. Sellers locked in losses. <em>Fix:</em> write your sell rules <em>before</em> the next crash.</li>
          <li><strong>4. Over-trading.</strong> Every trade has a tax bill and a cognitive cost. <em>Fix:</em> log every planned trade for 24 hours before executing.</li>
          <li><strong>5. Following tips from social media without research.</strong> See our deep dive on <Link to="/blog/wsb-stocks">WSB stocks</Link>. <em>Fix:</em> use tips as research starting points, not buy triggers.</li>
        </ul>
      </section>

      <section>
        <h2>Mistakes 6–10: structural mistakes</h2>
        <ul>
          <li><strong>6. Ignoring fees.</strong> 1% extra in fees over 30 years can cost you 25%+ of your final portfolio. <em>Fix:</em> use ETFs with expense ratios under 0.10%.</li>
          <li><strong>7. Lack of diversification.</strong> One bad position can wipe out years of gains. <em>Fix:</em> a single broad-market ETF gives you 500–10,000 stocks instantly.</li>
          <li><strong>8. Using leverage too early.</strong> Margin and options magnify losses as much as gains. <em>Fix:</em> no leverage until you've ridden through at least one bear market unleveraged.</li>
          <li><strong>9. Skipping tax-advantaged accounts.</strong> US: 401(k), IRA, Roth IRA. UK: ISA, SIPP. AUS: super. <em>Fix:</em> max these before touching a taxable account.</li>
          <li><strong>10. No plan.</strong> Without a written goal and contribution schedule, every market wobble feels like a decision. <em>Fix:</em> one page: target amount, monthly contribution, allocation, rebalance schedule.</li>
        </ul>
      </section>

      <section>
        <h2>What's the worst mistake of all?</h2>
        <p>
          Selling in a downturn. The historical record is brutal: every major bear market in the last 100 years was followed by a full recovery and then new highs. Investors who sold at the bottom missed the rebound and rarely got back in until the next peak.
          Buy-and-hold isn't glamorous, but it's mathematically dominant for most people.
        </p>
      </section>

      <section>
        <h2>How do you train yourself to avoid these?</h2>
        <p>
          Three habits that compound:
        </p>
        <ul>
          <li><strong>Practise on a simulator.</strong> Try <Link to="/blog/paper-trading">paper trading</Link> for a few months. Make your first ten mistakes there.</li>
          <li><strong>Build pattern recognition.</strong> Daily exposure to real charts and metrics — through <Link to="/">Wallstreetle</Link> or similar — sharpens instincts no book can teach.</li>
          <li><strong>Read the basics once.</strong> See our guides on <Link to="/blog/what-is-pe-ratio">P/E ratios</Link>, <Link to="/blog/what-is-market-cap">market cap</Link>, and <Link to="/blog/index-funds-vs-stocks">index funds vs stocks</Link>.</li>
        </ul>
      </section>
    </BlogArticleLayout>
  );
}
